import { apiErrorMessage } from '@/lib'
import { FormDialog, logger, SubtleLoader } from '@/ui'
import {
	entitlementIndexQueryKey,
	type PlanInterval,
	planInterval,
	subscriptionIndexQueryKey,
	useSubscriptionChangeTrialPlan,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlanStep } from './plan-step'

interface TrialPlanDialogProps {
	open: boolean
	onClose: () => void
	organisationId: string
	profileId: string
	trialEndsOn: string
	/** Picking a plan also resumes a cancelled trial. */
	isCancelled: boolean
}

export function TrialPlanDialog(
	{ open, onClose, organisationId, profileId, trialEndsOn, isCancelled }: TrialPlanDialogProps,
) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const [interval, setInterval] = useState<PlanInterval>(planInterval.monthly)
	const [error, setError] = useState<string | null>(null)

	const changePlan = useSubscriptionChangeTrialPlan({
		onMutate: () => setError(null),
		onSuccess: async (updated, { data }) => {
			logger.track('Subscription: trial plan picked', {
				category: 'billing',
				data: { tier: data.tier, interval: data.interval, resumed: isCancelled },
			})
			queryClient.setQueryData(subscriptionIndexQueryKey(organisationId), updated)
			await queryClient.invalidateQueries({ queryKey: entitlementIndexQueryKey(organisationId) })
			onClose()
		},
		onError: (caught) => {
			setError(apiErrorMessage(caught) ?? t('subscribe.trial.failed'))
			logger.error(caught, { category: 'billing', data: { action: 'change-trial-plan', organisationId } })
		},
	})

	return (
		<FormDialog open={open} onClose={onClose} title={t('subscribe.trial.planDialog.title')} maxWidth="lg">
			<p className="mb-6 text-sm text-grey-600">
				{t('subscribe.trial.planDialog.description', { date: trialEndsOn })}
			</p>
			{error
				? (
					<p
						role="alert"
						className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
					>
						{error}
					</p>
				)
				: null}
			{changePlan.isPending
				? <SubtleLoader delay={0} />
				: (
					<PlanStep
						profileId={profileId}
						interval={interval}
						onIntervalChange={setInterval}
						onChoose={(tier) =>
							changePlan.mutate({
								organisation: organisationId,
								profile: profileId,
								data: { tier, interval },
							})}
					/>
				)}
		</FormDialog>
	)
}
